import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type { Access, Context as AdminContext, App } from '../admin'
import type * as common from '../common'

import type { ZoneConfig } from './zone-config'

/**
 * An AI agent that can be deployed to zones and assigned to users.
 *
 * The {@link Agent.Id} identifies which Bedrock agent this represents.
 * The {@link Agent.ForUser} type is used in the `me` query hierarchy.
 */
export interface Agent extends common.Identified<Agent.Id>, common.Described {}

export namespace Agent {
  /**
   * Identifies an {@link Agent}
   */
  export enum Id {
    AttainIqExperimental = 'attainIqExperimental',
    Bch = 'bch',
    ChangeImpactAnalysis = 'changeImpactAnalysis',
    Dms = 'dms',
    DmsExcelGenerator = 'dmsExcelGenerator',
    DmsQueryGenerator = 'dmsQueryGenerator',
    DmsSchemaAssistant = 'dmsSchemaAssistant',
    DeckOrchestrator = 'deckOrchestrator',
    DiscoveryInsightsAnalyzer = 'discoveryInsightsAnalyzer',
    DolomiteGeneral = 'dolomiteGeneral',
    EmployeeUtilizationAssistant = 'employeeUtilizationAssistant',
    EffortChecker = 'effortChecker',
    EngagementCharterGenerator = 'engagementCharterGenerator',
    Gil = 'gil',
    Guide = 'guide',
    Mosaic = 'mosaic',
    Naming = 'naming',
    OcmChangeManagementKnowledgeBaseAssistant = 'ocmChangeManagementKnowledgeBaseAssistant',
    OcmAscendKnowledgeBaseAssistant = 'ocmAscendKnowledgeBaseAssistant',
    OcmAscendTranscriptAnalyzer = 'ocmAscendTranscriptAnalyzer',
    OrgChart = 'orgChart',
    PolicyAssistant = 'policyAssistant',
    PrincetonAscendArchitect = 'princetonAscendArchitect',
    ProjectPlanGenerator = 'projectPlanGenerator',
    ProjectRecommendation = 'projectRecommendation',
    ProposalGenerator = 'proposalGenerator',
    ProposalMatrixGenerator = 'proposalMatrixGenerator',
    ResearchPerformanceAnalysis = 'researchPerformanceAnalysis',
    StakeholderAnalysis = 'stakeholderAnalysis',
    Stm = 'stm',
    StmUnitTesting = 'stmUnitTesting',
    PptGenerator = 'pptGenerator',
    WorkPacketGenerator = 'workPacketGenerator',
  }

  /** Agents that only ever run on AgentCore, with no classic Bedrock agent. */
  export type AgentCoreOnlyId = Id.Dms | Id.DeckOrchestrator

  /** Agents that have a classic Bedrock agent behind them. */
  export type ClassicId = Exclude<Id, AgentCoreOnlyId>

  /**
   * Represents an agent assignment as returned by the `me` query.
   * Contains the agent's ID, the user's access level, and optional page context
   * narrowing where the agent is visible.
   */
  export interface ForUser {
    readonly id: Agent.Id
    readonly access: Access
    readonly pageContext?: ZoneConfig.PageContext
  }

  /**
   * Identifies the scope of an agent assignment.
   *
   * Extends {@link AdminContext.ForZone} (tenantId + zoneId) with
   * {@link Agent.Id} and an optional {@link App.Id}.
   */
  export interface Context extends AdminContext.ForZone {
    readonly agentId: Agent.Id
    readonly appId?: App.Id
  }
}

const AGENT_IDS: ReadonlySet<Agent.Id> = new Set(
  Object.values(Agent.Id) as Agent.Id[],
)

export namespace Agent {
  export namespace Id {
    export const ALL: readonly Id[] = Array.from(AGENT_IDS)
    export const isId = TypeChecker.Factory.oneOf(AGENT_IDS)
    export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)
  }
}
